import { Box, Button, Grid, useTheme } from "@mui/material";
import ShareLink from "./components/ShareLink";
import ClientInfoCard from "./components/ClientInfoCard";
import DocumentsSection from "./components/DocumentsSection";
import StreamSection from "./components/StreamSection";
import Sidebar from "./components/Sidebar";
import ServiceCard from "./components/ServiceCard";
import { useBookingData } from "../../../view-booking/hooks/UseBookingData";
import { DocumentItem } from "../../types/documents";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BookingData } from "../../../types/view-booking";
import sharemessagegraphicpattern from "../../../../stream-management/components/share-message/pattern.png";
import muzukurulogo from "../../../../../assets/muzukurublack.png";
import BookingInfoCard from "./components/BookingInfoCard";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../store/Index";
import { tokens } from "../../../../../theme";

type BookingStream = BookingData["streams"][number];
type UIStream = {
  id: string;
  title: string;
  date: string;
  time: string;
  locationName: string;
  locationAddress: string;
};

const BookingDetails = () => {
  const { bookingData, documents } = useBookingData();
  const { id } = useParams<{ id: string }>();
  const [docs, setDocs] = useState<DocumentItem[]>([]);

  useEffect(() => {
    setDocs(documents);
  }, [documents]);

  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const navigate = useNavigate();
  const [streamTab, setStreamTab] = useState(0);

  const user = useSelector((state: RootState) => state.auth.user);

  const streams = bookingData?.streams ?? [];
  const streamTabs = streams.map((_, idx) => `Stream ${idx + 1}`);
  const activeStream = streams[streamTab];
  const currentStreamId = activeStream?.id;
  

  const handleEdit = () => navigate(`/bookings/${id}/edit`);

  const handleViewStream = () => {
    if (!currentStreamId) return;
    navigate(`/stream-management/view/${currentStreamId}`);
  };

  const handleOpenCallsheet = () => {
    if (!currentStreamId) return;
    navigate(`/stream-management/view/${currentStreamId}/callsheet/`);
  };

  const handleOpenMemorialPage = () => {
    if (!id) return;

    const streamTitle = activeStream?.title || "";
    const prefix = "Stream for ";
    const nameForSlug = streamTitle.startsWith(prefix)
      ? streamTitle.slice(prefix.length)
      : streamTitle;

    const slug = encodeURIComponent(nameForSlug.trim().replace(/\s+/g, "-"));

    if (bookingData?.service === "Conference") {
      const conferenceSlug = encodeURIComponent(
        bookingData.conferenceDetails?.conference_title?.replace(/\s+/g, "-") ||
          "conference",
      );
      navigate(`/conference-page/${id}/${conferenceSlug}`);
      return;
    }

    if (bookingData?.service === "Church") {
      const churchTitle = encodeURIComponent(
        bookingData.churchDetails?.church_name?.replace(/\s+/g, "-") ||
          "church",
      );
      navigate(`/church-page/${id}/${churchTitle}`);
      return;
    }

    navigate(`/memorial-page/${id}/${slug}`);
  };

  const toUIStream = (s: BookingStream): UIStream => ({
    id: String(s.id ?? ""),
    title: s.title,
    date: s.date,
    time: `${s.start?.slice(0, 5)} - ${s.end?.slice(0, 5)}`,
    locationName: s.venueDetails?.name ?? "N/A",
    locationAddress: [s.addressOne, s.addressTwo, s.suburb, s.city]
      .filter((v): v is string => Boolean(v && v.trim()))
      .join(", "),
  });

  const uiStreams = streams.map(toUIStream);
  const uiActiveStream = uiStreams[streamTab];
  console.log(bookingData);


  return (
    <Box sx={{ width: "100%" }}>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          onClick={handleEdit}
          variant="contained"
          sx={{
            bgcolor: colors.primary[400],
            color: colors.primary[900],
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
          }}
        >
          Edit Booking
        </Button>
      </Box>

      <Grid container spacing={2} alignItems="stretch">
        <Grid size={{ xs: 12, md: 9 }} sx={{ display: "flex" }}>
          <Box sx={{ width: "100%" }}>
            <ShareLink
              streamLink={activeStream?.website_stream_link}
              fullName={bookingData?.beloved?.name || "N/A"}
              birthDate={bookingData?.beloved?.details?.dateOfBirth || "N/A"}
              deathDate={bookingData?.beloved?.details?.dateOfDeath || "N/A"}
              funeralDateText={activeStream?.date || "N/A"}
              venueText={
                [
                  activeStream?.venueDetails?.name,
                  activeStream?.addressOne,
                  activeStream?.addressTwo,
                  activeStream?.suburb,
                  activeStream?.city,
                ]
                  .filter((value): value is string =>
                    Boolean(value && value.trim()),
                  )
                  .join(", ") || "N/A"
              }
              imageUrl={bookingData?.beloved?.avatar || "/default-avatar.jpg"}
              patternSvgUrl={sharemessagegraphicpattern}
              funeralStartTime={activeStream?.start || "N/A"}
              livestreamedBy={muzukurulogo}
              serviceType={bookingData?.service}
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 3 }} sx={{ display: "flex" }}>
          <Box sx={{ width: "100%", height: "100%" }}>
            <Sidebar
              rating={bookingData?.completeness_score || 0}
              smartActions={[]}
              actionItems={[]}
              mode="ratingOnly"
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }} sx={{ display: "flex" }}>
          <Box sx={{ width: "100%" }}>
            <ClientInfoCard
              client={bookingData?.organiser}
              currentUserName={
                user?.first_name + " " + user?.last_name
              }           
              onViewAllBookings={() => navigate(`/contacts/${bookingData?.organiser?.contact_type}/view/${bookingData?.organiser?.id}`)}
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }} sx={{ display: "flex" }}>
          <Box sx={{ width: "100%" }}>
            <BookingInfoCard bookingData={bookingData} />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 3 }} sx={{ display: "flex" }}>
          <Box sx={{ width: "100%", height: "100%" }}>
            <Sidebar
              rating={70}
              smartActions={[]}
              actionItems={[]}
              mode="bookingSidebar"
              bookingData={bookingData}
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }} sx={{ display: "flex" }}>
          <Box sx={{ width: "100%" }}>
            <DocumentsSection
              documents={docs}
              bookingId={id ?? ""}
              onAddDocument={(newDoc) => setDocs((prev) => [newDoc, ...prev])}
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }} sx={{ display: "flex" }}>
          <Box sx={{ width: "100%" }}>
            <ServiceCard
              name={bookingData?.package?.name || ""}
              price={bookingData?.package?.price || 0}
              items={bookingData?.package?.items || []}
              total={bookingData?.package?.total || ""}
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 9 }}>
          {uiActiveStream && (
            <StreamSection
              streamTabs={streamTabs}
              stream={uiActiveStream}
              activeTab={streamTab}
              onTabChange={setStreamTab}
              onViewStream={handleViewStream}
              onOpenMemorialPage={handleOpenMemorialPage}
              onOpenCallsheet={handleOpenCallsheet}
              serviceType={bookingData?.service}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default BookingDetails;
